import { get } from "storefront/GrailedAPI/request";
import { Id } from "storefront/lib/Id";

/**
 * Represents the eligibility status for the "Grailed Pays For Shipping" (GPFS) feature.
 *
 * - `"control"`: The user is in the control group (not eligible for GPFS).
 * - `"variant"`: The user is in the variant group (eligible for GPFS).
 * - `null`: User does not qualify for the GPFS experiment or the eligibility is unknown.
 */
export type GpfsEligibility = "control" | "variant" | null;

export type GpfsExperimentGroup = "dnq" | "control" | "variant";

type Response = {
  data: {
    gpfsEligibility: GpfsEligibility;
  };
};

const getGpfsEligibility = async (userId: Id) =>
  get<Response>(`/api/users/${userId}/gpfs_eligibility`);

const unpack = (response: Response) => response.data.gpfsEligibility;

let apiResultCache: GpfsEligibility | undefined;
let inFlightPromise: Promise<GpfsEligibility> | null = null;

/**
 * Retrieves the GPFS (Grailed Pays For Shipping) eligibility status for a given user.
 * Utilizes caching to avoid redundant API calls and ensures only one in-flight request per user at a time.
 *
 * @param userId - The unique identifier of the user whose GPFS eligibility is being checked.
 * @returns A promise that resolves to the user's GPFS eligibility status.
 */
export function getGpfsEligibilityForUser(
  userId: Id,
): Promise<GpfsEligibility> {
  if (apiResultCache !== undefined) return Promise.resolve(apiResultCache);
  if (inFlightPromise) {
    return inFlightPromise;
  }

  inFlightPromise = getGpfsEligibility(userId)
    .then((data) => {
      apiResultCache = unpack(data);
      return apiResultCache;
    })
    .finally(() => {
      inFlightPromise = null;
    });

  return inFlightPromise;
}

export const resetGpfsEligibilityCache = () => {
  apiResultCache = undefined;
  inFlightPromise = null;
};
